import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTheme } from '../../context/PortalThemeContext';
import { PiCheck, PiCopy, PiMapPin, PiTruck, PiClock, PiPackage } from 'react-icons/pi';
import { TrackingStatus } from '../returns/TrackingStatus';
import './TrackingInfo.css';

const STATUS_CONFIG = {
  DELIVERED:     { label: 'Delivered',     subtitle: 'Your package has arrived',               variant: 'delivered' },
  IN_TRANSIT:    { label: 'In Transit',    subtitle: 'Your package is on the way',             variant: 'in-transit' },
  LABEL_CREATED: { label: 'Label Created', subtitle: 'Drop off your package at the carrier',   variant: 'pending' },
  NOT_FOUND:     { label: 'Unknown',       subtitle: 'Tracking information is not available',   variant: 'pending' },
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return timestamp;
  return (
    date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' \u00B7 ' +
    date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  );
};

const getEventIcon = (description, isDelivered) => {
  if (isDelivered) return <PiCheck size={20} color="white" />;
  const d = (description || '').toLowerCase();
  if (d.includes('out for delivery')) return <PiTruck size={16} />;
  if (d.includes('transit'))          return <PiClock size={16} />;
  if (d.includes('label'))            return <PiPackage size={16} />;
  if (d.includes('accepted'))         return <PiPackage size={16} />;
  return <PiMapPin size={16} />;
};

/**
 * Reusable timeline used by both TrackingInfo (order tracking) and
 * TrackReturnPage (return tracking). Accepts an array of events with
 * { timestamp, description, completed, location? }.
 */
export const TrackingProgress = ({ trackingEvents }) => {
  const theme = useTheme();

  if (!trackingEvents || trackingEvents.length === 0) {
    return <p className="tracking-no-events">No tracking available at this time.</p>;
  }

  return (
    <div>
      {trackingEvents.map((event, idx) => {
        const isFirst = idx === 0;
        const isLast = idx === trackingEvents.length - 1;
        const isDelivered = isFirst && event.completed &&
          (event.description || '').toLowerCase().includes('deliver');

        return (
          <div key={idx} className="tracking-timeline-entry">
            <div className="tracking-timeline-rail">
              <div
                className={`tracking-timeline-dot ${isDelivered ? 'delivered-dot' : 'standard-dot'}`}
                style={isDelivered ? { backgroundColor: theme.accentColor } : undefined}
              >
                {getEventIcon(event.description, isDelivered)}
              </div>
              {!isLast && <div className="tracking-timeline-line" />}
            </div>

            <div className="tracking-timeline-content">
              <p
                className={`tracking-event-desc ${isDelivered ? 'highlight' : ''}`}
                style={isDelivered ? { color: theme.accentColor } : undefined}
              >
                {event.description}
              </p>
              {event.timestamp && (
                <p className="tracking-event-time">{formatTimestamp(event.timestamp)}</p>
              )}
              {event.location && (
                <p className="tracking-event-location">{event.location}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TrackingInfo = ({ trackingInfo, onBack }) => {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);

  const statusKey = trackingInfo?.status || 'NOT_FOUND';
  const statusInfo = STATUS_CONFIG[statusKey] || STATUS_CONFIG.NOT_FOUND;
  const trackingNumber = trackingInfo?.trackingNumber || '';
  const carrier = trackingInfo?.carrier || 'Unknown';

  const copyTracking = () => {
    if (!trackingNumber) return;
    navigator.clipboard.writeText(trackingNumber).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="tracking-page" style={{ backgroundColor: theme.background }}>
      <Button
        variant="link"
        onClick={() => onBack()}
        className="tracking-back-link"
        style={{ color: theme.accentColor }}
      >
        ← Back
      </Button>

      <h1 className="tracking-page-title" style={{ color: theme.textDark }}>
        Tracking Info
      </h1>

      {/* Status Card */}
      <div className="tracking-status-card">
        <div className="tracking-status-header">
          <div
            className={`tracking-status-dot ${statusInfo.variant}`}
            style={statusInfo.variant === 'delivered' ? { backgroundColor: theme.accentColor } : undefined}
          >
            {statusInfo.variant === 'delivered' && <PiCheck size={20} color="white" />}
          </div>
          <div>
            <div className="tracking-status-label">{statusInfo.label}</div>
            <div className="tracking-status-subtitle">{statusInfo.subtitle}</div>
          </div>
        </div>

        {trackingNumber && (
          <div className="tracking-meta-row">
            <div className="tracking-meta-item">
              <div className="tracking-meta-label">
                TRACKING
                <PiCopy
                  size={14}
                  className="tracking-copy-icon"
                  onClick={copyTracking}
                  style={copied ? { color: theme.accentColor, opacity: 1 } : undefined}
                />
              </div>
              <div className="tracking-meta-value">{copied ? 'Copied!' : trackingNumber}</div>
            </div>
            <div className="tracking-meta-item tracking-meta-end">
              <div className="tracking-meta-label">CARRIER</div>
              <div className="tracking-meta-value">{carrier}</div>
            </div>
          </div>
        )}
      </div>

      {/* Shipment History */}
      <div className="tracking-history-card">
        <h2 className="tracking-history-title">Shipment History</h2>
        <TrackingProgress trackingEvents={trackingInfo?.history} />
      </div>
    </div>
  );
};

export default TrackingInfo;
